import './vendor/styles/bootstrap.scss';
import './vendor/styles/appwork.scss';
import './vendor/styles/theme-teldat.scss';
import './vendor/styles/colors.scss';
import './App.scss';

import React, { useCallback, useState } from 'react';
import I18nProvider from './i18n/i18nProvider';
import { BrowserRouter } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { persistor } from './store';
import ThemeProvider from 'assets/materialUIThemeProvider/ThemeProvider';
import { useEffect } from 'react';
import { ReactKeycloakProvider } from '@react-keycloak/web';
import Keycloak from 'keycloak-js';
import { GetConfig } from 'utils/config';
import Loader from 'shared/Loader';
import AuthenticatedApp from 'shared/AuthenticatedApp';
import { login } from 'app/crud/auth.crud';
import { useDispatch } from 'react-redux';
import * as auth from 'store/ducks/auth.duck';
import { setCookie } from 'utils/utils';
import { SetPolicyChanges } from 'store/ducks/policyChange.duck';

// flexWAN rig: ?idp=migedi|siteminder selects the Keycloak identity provider
// (idpHint). Absent → unchanged login-required behavior for plain localhost.
const idpHint =
  new URLSearchParams(window.location.search).get('idp') || undefined;

// flexWAN escape hatch: ?kc_client=<id> forces the Keycloak client id, overriding the
// host-derived one. Empty/absent → fall back to the host-derived client. (A SPA can't read
// request headers, so this is a query param.)
const kcClientOverride =
  new URLSearchParams(window.location.search).get('kc_client') || undefined;

function App() {
  const [keycloak, setKeycloak] = useState(null);

  const dispatch = useDispatch();

  useEffect(() => {
    GetConfig()
      .then((config) => config.json())
      .then((config) => {
        // BSSP-2006 — /api/info fields (host-aware client_id: BSP_<cif> on flexWAN hosts).
        const newKeycloak = new Keycloak({
          url: config.url,
          realm: config.realm,
          clientId: config.client_id,
        });
        setKeycloak(newKeycloak);
      });
  }, []);

  const updateSessionToken = useCallback(() => {
    setCookie('kc_session', keycloak.token);
    login(keycloak.idTokenParsed.email)
      .then((response) => {
        dispatch(auth.actions.login(response.data));
      })
      .catch(async () => {
        setCookie('kc_session', '', 'Expires=Thu, 01 Jan 1970 00:00:01 GMT;');
        dispatch(auth.actions.logout());
        dispatch(SetPolicyChanges(0));
        await keycloak.logout({
          redirectUri: window.location.origin,
        });
      });
  }, [dispatch, keycloak]);

  if (!keycloak) {
    return <Loader />;
  }

  return (
    <PersistGate persistor={persistor}>
      <ReactKeycloakProvider
        initOptions={{
          onLoad: idpHint ? 'check-sso' : 'login-required',
          checkLoginIframe: false,
        }}
        LoadingComponent={<Loader />}
        authClient={keycloak}
        onEvent={(event) => {
          if (event === 'onReady' && idpHint && !keycloak.authenticated) {
            // ?idp=<alias> → route the login to that IdP (MiGeDi/SiteMinder) via kc_idp_hint.
            // (To switch to a different portal user, use a fresh/incognito session — an existing SSO
            // session is reused, which is normal SSO behavior.)
            keycloak.login({ idpHint });
            return;
          }
          if (event === 'onAuthRefreshSuccess') {
            updateSessionToken();
          }
          if (event === 'onAuthSuccess') {
            dispatch(auth.actions.logout());
            dispatch(SetPolicyChanges(0));
            updateSessionToken();
          }
        }}
      >
        <BrowserRouter>
          <ThemeProvider>
            <I18nProvider>
              <AuthenticatedApp />
            </I18nProvider>
          </ThemeProvider>
        </BrowserRouter>
      </ReactKeycloakProvider>
    </PersistGate>
  );
}

export default App;
